import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import StatusChip from '../../changelog/components/StatusChip';
import useOnboardingSimulation from '../hooks/useOnboardingSimulation';
import { SIMULATION_DAYS, SIMULATION_STATUS } from '../onboardingAdminConfig';
import { formatDate } from '../../changelog/changelogAdminConfig';

const codeSx = { fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' };

/** Motivo do evento: rótulo do pulo (quando houver) e a explicação da regra. */
const reasonOf = (event) => [event.reason_label, event.reason].filter(Boolean).join(': ');

const SimulationEvent = ({ event }) => (
  <Box component="li" sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap', py: 0.5 }}>
    <Typography variant="body2" component="span" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
      {formatDate(event.date)} · dia {event.trial_day}
    </Typography>
    <Typography component="span" sx={{ ...codeSx, minWidth: 36 }}>{event.stage_code}</Typography>
    <StatusChip info={SIMULATION_STATUS[event.status] || { label: event.status, color: '#64748B', bg: '#F1F5F9' }} />
    {event.variant_code && <Typography component="span" sx={{ ...codeSx, color: 'primary.main' }}>{event.variant_code}</Typography>}
    <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
      {event.subject && <Typography variant="body2">{event.subject}</Typography>}
      {reasonOf(event) && <Typography variant="caption" color="text.secondary" component="div">{reasonOf(event)}</Typography>}
    </Box>
  </Box>
);

const AccountPlan = ({ account }) => (
  <Box sx={{ py: 1.5, borderTop: '1px solid #EEF1F6' }}>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{account.company_name || `Empresa ${account.company_id}`}</Typography>
      <Typography variant="caption" color="text.secondary">
        #{account.company_id} · {account.email} · dia {account.trial_day} do teste · termina em {formatDate(account.trial_ends_at)}
        {account.unsubscribed ? ' · descadastrada das dicas de uso' : ''}
      </Typography>
    </Box>
    {account.events.length === 0 ? (
      <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.5 }}>Nada no período.</Typography>
    ) : (
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
        {account.events.map((event, index) => <SimulationEvent key={`${event.date}-${event.stage}-${index}`} event={event} />)}
      </Box>
    )}
  </Box>
);

/**
 * Simulação dos próximos 7, 14 ou 30 dias: quem recebe o quê e por que cada etapa sai,
 * fica para depois ou é pulada, sem enviar nem gravar nada.
 * Supõe que a situação de cada conta continua a de hoje.
 */
function SimulationPanel() {
  const [assumeAll, setAssumeAll] = useState(true);
  const [days, setDays] = useState(SIMULATION_DAYS[0]);
  const { loading, error, result, simulate } = useOnboardingSimulation();

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Simulação da jornada</Typography>
          <Typography variant="body2" color="text.secondary">Nada é enviado. Supõe que cada conta continua como está hoje.</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Período" value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ width: 130 }}>
            {SIMULATION_DAYS.map((value) => <MenuItem key={value} value={value}>{value} dias</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Checkbox size="small" checked={assumeAll} onChange={(e) => setAssumeAll(e.target.checked)} />}
            label={<Typography variant="body2">Como se todos estivessem ligados</Typography>}
          />
          <Button variant="outlined" size="small" startIcon={<PlayArrowOutlinedIcon />} disabled={loading} onClick={() => simulate(assumeAll, days)}>
            {loading ? 'Simulando...' : 'Simular'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Em {result.days} dias a partir de {formatDate(result.start)}: <strong>{result.total}</strong> {result.total === 1 ? 'e-mail' : 'e-mails'} para <strong>{result.accounts.length}</strong> {result.accounts.length === 1 ? 'conta' : 'contas'}
            {result.assume_all_enabled ? ', considerando todos os modelos ligados' : ', só com os modelos ligados'}
            {!result.sequence_enabled && '. A sequência está desligada: hoje nada disso sai.'}
          </Typography>
          {result.accounts.map((account) => <AccountPlan key={account.company_id} account={account} />)}
        </Box>
      )}
    </Paper>
  );
}

export default SimulationPanel;
